import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { format, isToday, isYesterday } from "date-fns";
import {
  ArrowLeft,
  Mic,
  Square,
  Paperclip,
  Send,
  Search,
  X,
  FileText,
  Download,
  MessageSquare,
} from "lucide-react";
import { AdminPopup, Avatar, Empty, Spinner } from "../../../dashboardAdmin/sidebar/adminLayout" ; // adapte le chemin si besoin
import "./messages.css"; // même fichier que celui de l'admin (nouvelle version)

const ADMIN_EMAIL = "admin1@gmail.com";
const POLL_MS = 10000; 

/* ── Helpers (hors du composant) ── */
const getFileType = (filePath) => {
  if (!filePath) return null;
  const extension =
    typeof filePath === "string"
      ? filePath.split(".").pop().toLowerCase()
      : filePath.name.split(".").pop().toLowerCase();
  if (["webm", "mp3", "wav"].includes(extension)) return "audio";
  if (["jpeg", "jpg", "png", "gif"].includes(extension)) return "image";
  if (["pdf"].includes(extension)) return "pdf";
  return "other";
};

const fullName = (u) =>
  `${u?.name || ""} ${u?.surname || ""}`.trim() || "Unknown user";

const fileLabel = (path) => String(path || "").split(/[\\/]/).pop();

/* Date du dernier message d'une conversation (0 si aucun) */
const lastActivity = (conv) => {
  const msgs = (conv && conv.messages) || [];
  let last = 0;
  msgs.forEach((m) => {
    const t = new Date(m.createdAt).getTime();
    if (t > last) last = t;
  });
  return last;
};

/* Dernier message d'une conversation (null si aucun) */
const lastMessageOf = (conv) => {
  const msgs = (conv && conv.messages) || [];
  return msgs.reduce(
    (last, m) =>
      !last || new Date(m.createdAt) > new Date(last.createdAt) ? m : last,
    null
  );
};

/* Aperçu texte du dernier message */
const previewText = (m) => {
  if (!m) return "";
  if (m.content) return m.content;
  if (m.fileType === "audio") return "Voice message";
  if (m.fileType === "image") return "Image";
  if (m.fileType === "pdf") return "PDF document";
  return m.filePath ? "File" : "";
};

/* Heure si aujourd'hui, sinon jj/mm */
const shortTime = (d) =>
  isToday(new Date(d))
    ? format(new Date(d), "HH:mm")
    : format(new Date(d), "dd/MM");

const dayLabel = (d) => {
  const date = new Date(d);
  if (isToday(date)) return "Today";
  if (isYesterday(date)) return "Yesterday";
  return format(date, "dd MMM yyyy");
};

const matchesSearch = (u, q) =>
  !q ||
  (u?.email || "").toLowerCase().includes(q) ||
  (u?.name || "").toLowerCase().includes(q) ||
  (u?.surname || "").toLowerCase().includes(q);

const Message = ({ onClose }) => {
  const [conversations, setConversations] = useState({});
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sendError, setSendError] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [view, setView] = useState("conversations");
  const [selectedImage, setSelectedImage] = useState(null);
  const [file, setFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [sending, setSending] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const messagesRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
  const FILES_BASE = API_URL.replace(/\/api\/?$/, "");
  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  /* ── Conversations (silent = rafraîchissement automatique) ── */
  const fetchConversations = async (silent = false) => {
    const token = localStorage.getItem("token");
    if (!token) {
      if (!silent) setError("No authentication token found. Please log in.");
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(
        `${API_URL}/messages/admin/conversations`,
        { headers: authHeaders() }
      );
      const updatedConversations = Object.fromEntries(
        Object.entries(response.data || {}).map(([userId, conv]) => [
          userId,
          {
            ...conv,
            messages: (conv.messages || []).map((msg) => ({
              ...msg,
              fileType: msg.filePath ? getFileType(msg.filePath) : null,
              sender: msg.senderEmail === ADMIN_EMAIL ? "admin" : "user",
            })),
          },
        ])
      );
      setConversations(updatedConversations);
    } catch (err) {
      console.error("Fetch conversations error:", err.response || err.message);
      if (err.response && err.response.status === 401) {
        setError("Token expired or invalid. Please log in again.");
        localStorage.removeItem("token");
        window.location.href = "/login";
      } else if (!silent) {
        setError("Failed to fetch conversations.");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Utilisateurs ── */
  const fetchUsers = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("No authentication token found. Please log in.");
      setUsers([]);
      return;
    }
    setIsLoadingUsers(true);
    try {
      const response = await axios.get(
        `${API_URL}/profiles/users-with-status`,
        { headers: authHeaders() }
      );
      setUsers(response.data || []);
      setError(null);
    } catch (err) {
      console.error("Fetch users error:", err.response || err.message);
      setError(err.response?.data?.message || "Failed to fetch users.");
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Rafraîchissement automatique (supprime ce bloc si tu n'en veux pas) */
  useEffect(() => {
    const id = setInterval(() => fetchConversations(true), POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Navigation ── */
  const handleViewChange = (newView) => {
    setView(newView);
    if (newView === "users" && !users.length) fetchUsers();
  };

  const handleUserSelect = (userId) => {
    setSelectedUserId(userId);
    setSendError(null);
  };

  /* ── Enregistrement vocal ── */
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        setFile(null);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current.start();
      setFile(null);
      setAudioBlob(null);
      setIsRecording(true);
    } catch (err) {
      console.error("Erreur lors de l'accès au microphone:", err);
      alert(
        "Unable to access the microphone. Please check your browser permissions."
      );
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setAudioBlob(null);
    }
    e.target.value = "";
  };

  const clearAttachment = () => {
    setFile(null);
    setAudioBlob(null);
  };

  /* ── Envoi d'un message ── */
  const handleSendMessage = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setSendError("No authentication token found. Please log in.");
      return;
    }
    if (!selectedUserId || (!newMessage.trim() && !file && !audioBlob)) {
      setSendError("Write a message or add a file / voice message first.");
      return;
    }
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("recipientId", selectedUserId);
      if (newMessage) formData.append("content", newMessage);
      if (file) formData.append("file", file);
      if (audioBlob) formData.append("file", audioBlob, "voice-message.webm");
      formData.append("senderEmail", ADMIN_EMAIL);

      const response = await axios.post(
        `${API_URL}/messages/admin/send/${selectedUserId}`,
        formData,
        {
          headers: {
            ...authHeaders(),
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setConversations((prev) => {
        const userConversation = prev[selectedUserId] || {
          user: users.find((u) => u._id === selectedUserId) || {},
          messages: [],
        };
        const newMessageData = {
          ...response.data,
          fileType: file ? getFileType(file.name) : audioBlob ? "audio" : null,
          sender: "admin",
        };
        return {
          ...prev,
          [selectedUserId]: {
            ...userConversation,
            messages: [...userConversation.messages, newMessageData],
          },
        };
      });

      setNewMessage("");
      setFile(null);
      setAudioBlob(null);
      setSendError(null);
    } catch (err) {
      console.error("Send message error:", err.response || err.message);
      setSendError(
        `Failed to send message: ${err.response?.data?.details || err.message}`
      );
    } finally {
      setSending(false);
    }
  };

  /* ── Dernier message / dernière activité par utilisateur ── */
  const lastByUser = useMemo(() => {
    const map = {};
    Object.entries(conversations).forEach(([id, conv]) => {
      map[id] = lastActivity(conv);
    });
    return map;
  }, [conversations]);

  const lastMsgByUser = useMemo(() => {
    const map = {};
    Object.entries(conversations).forEach(([id, conv]) => {
      map[id] = lastMessageOf(conv);
    });
    return map;
  }, [conversations]);

  /* ── Listes triées : le dernier message en premier ── */
  const sortedUsers = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return users
      .filter((u) => matchesSearch(u, q))
      .sort(
        (a, b) =>
          (lastByUser[b._id] || 0) - (lastByUser[a._id] || 0) ||
          (a.name || "").localeCompare(b.name || "")
      );
  }, [users, searchTerm, lastByUser]);

  const sortedConversations = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return Object.entries(conversations)
      .filter(([, conv]) => matchesSearch(conv.user, q))
      .sort(([a], [b]) => (lastByUser[b] || 0) - (lastByUser[a] || 0));
  }, [conversations, searchTerm, lastByUser]);

  /* ── Conversation affichée ── */
  const selectedUser = users.find((u) => u._id === selectedUserId);
  const selectedConv = selectedUserId
    ? conversations[selectedUserId] || {
        user: selectedUser || {},
        messages: [],
      }
    : null;
  const chatUser =
    selectedConv && selectedConv.user && Object.keys(selectedConv.user).length
      ? selectedConv.user
      : selectedUser || {};
  const messageCount = selectedConv ? selectedConv.messages.length : 0;

  /* Défile en bas à l'ouverture et à chaque nouveau message */
  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [selectedUserId, messageCount]);

  /* Aperçu du message vocal */
  const audioUrl = useMemo(
    () => (audioBlob ? URL.createObjectURL(audioBlob) : null),
    [audioBlob]
  );
  useEffect(
    () => () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    },
    [audioUrl]
  );

  const handleEscape = () => {
    if (selectedImage) return setSelectedImage(null);
    onClose?.();
  };

  const canSend = !sending && !isRecording && (newMessage.trim() || file || audioBlob);

  /* ── Rendu d'une ligne de la liste ── */
  const renderItem = (id, user, lastMsg) => (
    <div
      key={id}
      className={`msg-item ${selectedUserId === id ? "active" : ""}`}
      onClick={() => handleUserSelect(id)}
    >
      <Avatar user={user} />
      <div className="msg-item-main">
        <div className="msg-item-top">
          <span className="msg-item-name">{fullName(user)}</span>
          {lastMsg && (
            <span className="msg-item-time">{shortTime(lastMsg.createdAt)}</span>
          )}
        </div>
        <div className="msg-item-bottom">
          <span className="msg-item-preview">
            {lastMsg
              ? `${lastMsg.sender === "admin" ? "You: " : ""}${previewText(
                  lastMsg
                )}`
              : user?.email || "No messages yet"}
          </span>
        </div>
      </div>
    </div>
  );

  /* ── Pièce jointe d'un message ── */
  const renderAttachment = (message) => {
    if (!message.filePath) return null;
    const url = `${FILES_BASE}/uploads/${message.filePath}`;

    if (message.fileType === "audio") {
      return (
        <div className="msg-attachment">
          <audio controls className="msg-audio" src={url} />
        </div>
      );
    }
    if (message.fileType === "image") {
      return (
        <div className="msg-attachment">
          <button
            type="button"
            className="msg-image-btn"
            onClick={() => setSelectedImage(message.filePath)}
            aria-label="Enlarge image"
          >
            <img src={url} alt="attachment" className="msg-image" />
          </button>
        </div>
      );
    }
    const isPdf = message.fileType === "pdf";
    return (
      <div className="msg-attachment">
        <a
          className="msg-file"
          href={url}
          {...(isPdf
            ? { target: "_blank", rel: "noopener noreferrer" }
            : { download: true })}
        >
          <FileText />
          <span className="msg-file-name">{fileLabel(message.filePath)}</span>
          {isPdf ? null : <Download />}
        </a>
      </div>
    );
  };

  /* ── Fil de discussion groupé par jour ── */
  const renderThread = () => {
    let prevDay = null;
    return selectedConv.messages.map((message) => {
      const dayKey = format(new Date(message.createdAt), "yyyy-MM-dd");
      const showDay = dayKey !== prevDay;
      prevDay = dayKey;
      const mine = message.sender === "admin";

      return (
        <React.Fragment key={message._id}>
          {showDay && (
            <div className="msg-day">{dayLabel(message.createdAt)}</div>
          )}
          <div className={`msg-row ${mine ? "mine" : "theirs"}`}>
            <div className="msg-bubble">
              {message.content && <p className="msg-text">{message.content}</p>}
              {renderAttachment(message)}
              <span className="msg-time">
                {format(new Date(message.createdAt), "HH:mm")}
              </span>
            </div>
          </div>
        </React.Fragment>
      );
    });
  };

  /* ── États plein écran ── */
  if (loading) {
    return (
      <AdminPopup onClose={onClose} onEscape={handleEscape}>
        <div className="msg-state">
          <Spinner>Loading messages...</Spinner>
        </div>
      </AdminPopup>
    );
  }

  if (error) {
    return (
      <AdminPopup onClose={onClose} onEscape={handleEscape}>
        <div className="msg-state">
          <Empty>
            <span>{error}</span>
            <div className="msg-state-actions">
              <button
                className="adm-btn primary"
                onClick={() => {
                  setError(null);
                  fetchConversations();
                  fetchUsers();
                }}
              >
                Retry
              </button>
              <button className="adm-cancel-btn" onClick={onClose}>
                Close
              </button>
            </div>
          </Empty>
        </div>
      </AdminPopup>
    );
  }

  return (
    <AdminPopup onClose={onClose} onEscape={handleEscape}>
      <div className={`msg-layout ${selectedUserId ? "has-chat" : ""}`}>
        {/* ───── Colonne de gauche ───── */}
        <aside className="msg-side">
          <div className="msg-side-head">
            <button className="adm-back" onClick={onClose} aria-label="Close">
              <ArrowLeft /> Back
            </button>
            <h3>Messages</h3>
          </div>

          <div className="msg-tools">
            <div className="adm-searchbox">
              <Search />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="adm-searchbox-clear"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                >
                  <X />
                </button>
              )}
            </div>

            <div className="adm-filters">
              {[
                ["conversations", "Conversations", Object.keys(conversations).length],
                ["users", "Contacts", users.length],
              ].map(([key, label, count]) => (
                <button
                  key={key}
                  className={`adm-chip ${view === key ? "active" : ""}`}
                  onClick={() => handleViewChange(key)}
                >
                  {label}
                  <span className="adm-chip-count">{count}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="msg-list">
            {view === "users" ? (
              isLoadingUsers ? (
                <Spinner>Loading contacts...</Spinner>
              ) : sortedUsers.length === 0 ? (
                <p className="msg-list-empty">No contacts found.</p>
              ) : (
                sortedUsers.map((user) =>
                  renderItem(user._id, user, lastMsgByUser[user._id])
                )
              )
            ) : sortedConversations.length === 0 ? (
              <p className="msg-list-empty">
                No conversations yet. Pick someone in Contacts to start one.
              </p>
            ) : (
              sortedConversations.map(([userId, conversation]) =>
                renderItem(userId, conversation.user, lastMsgByUser[userId])
              )
            )}
          </div>
        </aside>

        {/* ───── Zone de discussion ───── */}
        <section className="msg-chat">
          {selectedConv ? (
            <>
              <div className="msg-chat-head">
                <button
                  className="adm-icon-btn msg-back"
                  onClick={() => setSelectedUserId(null)}
                  aria-label="Back to list"
                >
                  <ArrowLeft />
                </button>
                <Avatar user={chatUser} />
                <div className="msg-chat-info">
                  <p className="msg-chat-name">{fullName(chatUser)}</p>
                  <p className="msg-chat-email">
                    {chatUser.email || "No email"}
                  </p>
                </div>
              </div>

              <div className="msg-thread" key={selectedUserId} ref={messagesRef}>
                {selectedConv.messages.length > 0 ? (
                  renderThread()
                ) : (
                  <div className="msg-placeholder">
                    <MessageSquare />
                    <span>No messages yet. Start the conversation!</span>
                  </div>
                )}
              </div>

              {sendError && (
                <div className="msg-error" role="alert">
                  {sendError}
                </div>
              )}

              {(isRecording || file || audioBlob) && (
                <div className="msg-attach">
                  {isRecording ? (
                    <>
                      <span className="msg-rec-dot" />
                      <span className="msg-attach-name">Recording...</span>
                    </>
                  ) : file ? (
                    <>
                      <FileText size={16} />
                      <span className="msg-attach-name">{file.name}</span>
                    </>
                  ) : (
                    <audio controls src={audioUrl} key={audioUrl} />
                  )}
                  {!isRecording && (
                    <button
                      className="adm-icon-btn sm"
                      onClick={clearAttachment}
                      aria-label="Remove attachment"
                    >
                      <X />
                    </button>
                  )}
                </div>
              )}

              <div className="msg-composer">
                <input
                  type="file"
                  id="msg-file-input"
                  accept="image/*,application/pdf,audio/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <label
                  htmlFor="msg-file-input"
                  className="adm-icon-btn msg-pick"
                  title="Attach a file"
                >
                  <Paperclip />
                </label>
                <button
                  className={`adm-icon-btn ${isRecording ? "recording" : ""}`}
                  onClick={isRecording ? stopRecording : startRecording}
                  title={isRecording ? "Stop recording" : "Record a voice message"}
                  aria-label={isRecording ? "Stop recording" : "Record a voice message"}
                >
                  {isRecording ? <Square /> : <Mic />}
                </button>
                <input
                  type="text"
                  className="msg-input"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && canSend && handleSendMessage()}
                  placeholder="Type your message..."
                  disabled={isRecording}
                />
                <button
                  className="adm-btn primary msg-send"
                  onClick={handleSendMessage}
                  disabled={!canSend}
                  aria-label="Send"
                >
                  <Send />
                  <span className="msg-send-text">Send</span>
                </button>
              </div>
            </>
          ) : (
            <div className="msg-placeholder">
              <MessageSquare />
              <span>Select a conversation to start messaging</span>
            </div>
          )}
        </section>
      </div>

      {/* Image agrandie */}
      {selectedImage && (
        <div className="msg-lightbox" onMouseDown={() => setSelectedImage(null)}>
          <button
            className="adm-close-btn msg-lightbox-close"
            onClick={() => setSelectedImage(null)}
            aria-label="Close"
          >
            ×
          </button>
          <img
            src={`${FILES_BASE}/uploads/${selectedImage}`}
            alt="Enlarged"
            onMouseDown={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </AdminPopup>
  );
};

export default Message;